'use client';

export const dynamic = 'force-dynamic';

import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	useRouter,
	useSearchParams,
} from 'next/navigation';
import Link from 'next/link';
import api from '@/utils/api';
import {
	Search,
	ChevronLeft,
	ChevronRight,
	Star,
	ShoppingCart,
	Filter,
	X,
	Store,
	Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { BUSINESS_TYPES } from '@/constants/businessTypes';

/* --------------------
   Types & Helpers
   -------------------- */
type Product = {
	_id: string;
	name: string;
	price: number;
	shortDescription?: string;
	images?: { url?: string }[] | string[];
	store?: { _id: string; name: string } | string;
	stock?: number;
	rating?: number;
	category?: { _id: string; name: string } | string | null; // Handle populated object or string ID
};

type Category = {
	_id: string;
	name: string;
	slug: string;
};

const DEFAULT_LIMIT = 12;

function safeNumber(
	v: string | null | undefined,
	fallback = 0,
) {
	if (!v) return fallback;
	const n = Number(v);
	return Number.isFinite(n) ? n : fallback;
}

/* --------------------
   Main Component
   -------------------- */
export default function ProductsExplorerPage() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const { user } = useAuth();

	// --- State Initialization ---
	// --- State Initialization ---
	const [q, setQ] = useState(
		() => searchParams.get('q') ?? '',
	);

	// Stores the Category ID now, not the name
	const [category, setCategory] = useState<string | null>(
		() => searchParams.get('category'),
	);

	const [minPrice, setMinPrice] = useState<number | ''>(
		() => {
			const v = searchParams.get('minPrice');
			return v ? Number(v) : '';
		},
	);
	const [maxPrice, setMaxPrice] = useState<number | ''>(
		() => {
			const v = searchParams.get('maxPrice');
			return v ? Number(v) : '';
		},
	);
	const [storeId, setStoreId] = useState<string | null>(
		() => searchParams.get('storeId'),
	);
	const [sort, setSort] = useState(
		() => searchParams.get('sort') ?? 'newest',
	);
	const [page, setPage] = useState(() =>
		Math.max(1, safeNumber(searchParams.get('page'), 1)),
	);

	// --- Data State ---
	const [products, setProducts] = useState<Product[]>([]);
	const [total, setTotal] = useState(0);
	const [categories, setCategories] = useState<Category[]>(
		[],
	); // Now stores objects
	const [loading, setLoading] = useState(true);
	const [showMobileFilters, setShowMobileFilters] =
		useState(false);

	// --- Cart & Refs ---
	const { addItem } = useCart();
	const searchRef = useRef<number | null>(null);
	const [recentlyAdded, setRecentlyAdded] = useState<
		Record<string, boolean>
	>({});

	// --- Data Fetching ---

	// 1. Helper to convert UI sort keys to Mongoose sort strings
	const getBackendSort = (uiSort: string) => {
		switch (uiSort) {
			case 'price_asc':
				return 'price'; // Low to High
			case 'price_desc':
				return '-price'; // High to Low
			case 'popular':
				return '-sold'; // Assuming you track 'sold', else fallback
			case 'newest':
			default:
				return '-createdAt'; // Default to newest
		}
	};

	const buildQueryParams = useCallback(() => {
		const params: Record<string, any> = {
			page,
			limit: DEFAULT_LIMIT,
			sort: getBackendSort(sort), // Transform sort here
		};

		if (q) params.q = q;

		if (category) {
			if (BUSINESS_TYPES.includes(category)) {
				params.businessType = category;
			} else {
				params.category = category;
			}
		}

		if (storeId) params.storeId = storeId;
		if (minPrice !== '') params.minPrice = String(minPrice);
		if (maxPrice !== '') params.maxPrice = String(maxPrice);

		return params;
	}, [
		q,
		category,
		minPrice,
		maxPrice,
		storeId,
		sort,
		page,
	]);

	const fetchProducts = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildQueryParams();
			const resp = await api.get('/products', { params });
			const data =
				resp.data?.data ?? resp.data?.products ?? [];
			const meta = resp.data?.meta ?? {
				total: data.length,
			};

			let productList = Array.isArray(data) ? data : [];

			// Filter out own products if vendor is logged in
			if (user?.id) {
				productList = productList.filter((p: any) => {
					// Check if product store owner matches current user
					const ownerId = p.store?.owner?._id || p.store?.owner;
					// Some endpoints might populate differently, handle defensive check
					// If store is just ID, we can't filter by owner unless we fetch store details, 
					// but usually 'product' payload has populated store.
					// If strictly p.store.owner is the user ID:
					return String(ownerId) !== String(user.id);
				});
			}

			setProducts(productList);
			setTotal(Number(meta.total ?? 0));
		} catch (err) {
			console.error('Failed fetching products', err);
			setProducts([]);
		} finally {
			setLoading(false);
		}
	}, [buildQueryParams]);

	// --- Fetch Categories (Removed, using static BUSINESS_TYPES) ---
	/*
	useEffect(() => {
		const fetchCats = async () => {
			try {
				const resp = await api.get('/categories/all');
				const cats = Array.isArray(resp.data)
					? resp.data
					: resp.data?.categories || [];
				setCategories(cats);
			} catch (e) {
				console.error('Error fetching categories', e);
			}
		};
		fetchCats();
	}, []);
	*/

	console.log('Categories:', categories);

	// --- Explicit Search (Removed Debounce) ---

	const handleSearch = () => {
		if (page === 1) {
			syncUrl();
			fetchProducts(); // Explicitly fetch on search submit
		} else {
			setPage(1);
		}
	};

	// --- Effects ---
	const syncUrl = useCallback(() => {
		const params = new URLSearchParams(searchParams.toString());
		if (q) params.set('q', q); else params.delete('q');
		if (category) params.set('category', category);
		if (storeId) params.set('storeId', storeId);
		if (minPrice !== '')
			params.set('minPrice', String(minPrice));
		if (maxPrice !== '')
			params.set('maxPrice', String(maxPrice));
		if (sort) params.set('sort', sort);
		if (page > 1) params.set('page', String(page));

		const url = params.toString()
			? `/products?${params.toString()}`
			: '/products';

		// Only replace if URL actually changed to avoid clearing params on hydration
		if (url) router.replace(url, { scroll: false });
	}, [
		q,
		category,
		storeId,
		minPrice,
		maxPrice,
		sort,
		page,
		router,
		searchParams,
	]);


	// Only auto-fetch for non-text filters
	// If page/sort/category changes, we usually want immediate fetch.
	useEffect(() => {
		fetchProducts();
		syncUrl();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// q, // REMOVED: Don't fetch on typing
		category,
		storeId,
		minPrice,
		maxPrice,
		sort,
		page,
	]);

	// --- Handlers ---
	const handleSearchChange = (v: string) => {
		setQ(v);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	const handleAddToCart = (
		p: Product,
		e: React.MouseEvent,
	) => {
		e.preventDefault();
		e.stopPropagation();

		const imageUrl = Array.isArray(p.images)
			? (p.images[0] as any)?.url ?? p.images[0]
			: p.images;
		const storeObj =
			typeof p.store === 'object'
				? p.store
				: { _id: 'unknown', name: 'Unknown Store' };

		addItem(
			{
				id: p._id,
				name: p.name,
				price: p.price,
				image: typeof imageUrl === 'string' ? imageUrl : '',
				storeId: storeObj?._id,
				storeName: storeObj?.name,
			},
			1,
		);

		setRecentlyAdded((prev) => ({
			...prev,
			[p._id]: true,
		}));
		setTimeout(
			() =>
				setRecentlyAdded((prev) => {
					const copy = { ...prev };
					delete copy[p._id];
					return copy;
				}),
			1500,
		);
	};

	const clearAllFilters = () => {
		setQ('');
		setCategory(null);
		setMinPrice('');
		setMaxPrice('');
		setStoreId(null);
		setSort('newest');
		setPage(1);
		setShowMobileFilters(false);
	};

	return (
		<div className="min-h-screen bg-[#F8F9FA]">
			{/* Top Bar */}
			<div className="bg-white border-b sticky top-0 z-30 shadow-sm">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
						<div className="flex items-center justify-between">
							<h1 className="text-2xl font-bold text-[#051449]">
								Marketplace
							</h1>
							<button
								onClick={() => setShowMobileFilters(true)}
								className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
							>
								<Filter className="w-5 h-5" />
							</button>
						</div>

						<div className="flex flex-1 md:max-w-2xl gap-2">
							<div className="relative flex-1">
								<button
									onClick={handleSearch}
									className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#051449] transition-colors"
								>
									<Search className="w-4 h-4" />
								</button>
								<input
									type="text"
									placeholder="Search products..."
									value={q}
									onChange={(e) =>
										handleSearchChange(e.target.value)
									}
									onKeyDown={handleKeyDown}
									className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#051449]/20 focus:border-[#051449] outline-none transition-all"
								/>
							</div>
							<select
								value={sort}
								onChange={(e) => {
									setSort(e.target.value);
									setPage(1);
								}}
								className="hidden md:block px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 cursor-pointer focus:ring-2 focus:ring-[#051449]/20 outline-none"
							>
								<option value="newest">
									Newest Arrivals
								</option>
								<option value="price_asc">
									Price: Low to High
								</option>
								<option value="price_desc">
									Price: High to Low
								</option>
								<option value="popular">
									Most Popular
								</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="flex gap-8">
					{/* --- Sidebar Filters (Desktop) --- */}
					<aside className="hidden md:block w-64 flex-shrink-0 space-y-8">
						<div>
							<h3 className="text-sm font-bold text-[#051449] uppercase tracking-wider mb-4">
								Categories
							</h3>
							<div className="space-y-2">
								<button
									onClick={() => {
										setCategory(null);
										setPage(1);
									}}
									className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
										!category
											? 'bg-[#051449] text-white font-medium'
											: 'text-gray-600 hover:bg-gray-100'
									}`}
								>
									All Categories
								</button>
								{BUSINESS_TYPES.map((c) => (
									<button
										key={c}
										onClick={() => {
											setCategory(
												c === category ? null : c,
											);
											setPage(1);
										}}
										className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
											c === category
												? 'bg-[#051449] text-white font-medium'
												: 'text-gray-600 hover:bg-gray-100'
										}`}
									>
										{c}
									</button>
								))}
							</div>
						</div>

						<div>
							<h3 className="text-sm font-bold text-[#051449] uppercase tracking-wider mb-4">
								Price Range
							</h3>
							<div className="flex items-center gap-2">
								<input
									type="number"
									placeholder="Min"
									value={minPrice}
									onChange={(e) => {
										setMinPrice(
											e.target.value === ''
												? ''
												: Number(e.target.value),
										);
										setPage(1);
									}}
									className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-[#051449]"
								/>
								<span className="text-gray-400">-</span>
								<input
									type="number"
									placeholder="Max"
									value={maxPrice}
									onChange={(e) => {
										setMaxPrice(
											e.target.value === ''
												? ''
												: Number(e.target.value),
										);
										setPage(1);
									}}
									className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-[#051449]"
								/>
							</div>
						</div>

						{storeId && (
							<div className="bg-blue-50 p-4 rounded-xl">
								<p className="text-xs text-blue-600 font-semibold mb-2">
									FILTERING BY STORE
								</p>
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium text-blue-900 truncate">
										Current Store
									</span>
									<button
										onClick={() => setStoreId(null)}
										className="text-xs text-red-500 hover:underline"
									>
										Clear
									</button>
								</div>
							</div>
						)}

						<button
							onClick={clearAllFilters}
							className="w-full py-2 text-sm text-gray-500 hover:text-[#051449] border border-gray-200 hover:border-[#051449] rounded-lg transition-colors"
						>
							Reset All Filters
						</button>
					</aside>

					{/* --- Product Grid --- */}
					<main className="flex-1">
						{loading ? (
							<div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
								{Array.from({ length: 8 }).map((_, i) => (
									<div
										key={i}
										className="bg-white rounded-2xl p-4 h-80 animate-pulse"
									>
										<div className="bg-gray-200 h-48 rounded-xl mb-4" />
										<div className="bg-gray-200 h-4 w-3/4 rounded mb-2" />
										<div className="bg-gray-200 h-4 w-1/2 rounded" />
									</div>
								))}
							</div>
						) : products.length > 0 ? (
							<>
								<div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
									{products.map((p) => (
										<ProductCard
											key={p._id}
											product={p}
											onAddToCart={(e) =>
												handleAddToCart(p, e)
											}
											isAdded={!!recentlyAdded[p._id]}
										/>
									))}
								</div>

								{/* Pagination */}
								<div className="mt-12 flex items-center justify-center gap-4">
									<button
										onClick={() =>
											setPage((p) => Math.max(1, p - 1))
										}
										disabled={page <= 1}
										className="p-2 rounded-full border hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
									>
										<ChevronLeft className="w-5 h-5" />
									</button>
									<span className="text-sm font-medium text-gray-600">
										Page {page} of{' '}
										{Math.ceil(total / DEFAULT_LIMIT)}
									</span>
									<button
										onClick={() => setPage((p) => p + 1)}
										disabled={
											page >=
											Math.ceil(total / DEFAULT_LIMIT)
										}
										className="p-2 rounded-full border hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
									>
										<ChevronRight className="w-5 h-5" />
									</button>
								</div>
							</>
						) : (
							<div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
								<div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
									<Search className="w-8 h-8 text-gray-400" />
								</div>
								<h3 className="text-lg font-bold text-gray-900">
									No products found
								</h3>
								<p className="text-gray-500 mt-2">
									Try adjusting your search or filters
								</p>
								<button
									onClick={clearAllFilters}
									className="mt-6 px-6 py-2 bg-[#051449] text-white rounded-lg hover:bg-blue-900 transition-colors"
								>
									Clear Filters
								</button>
							</div>
						)}
					</main>
				</div>
			</div>

			{/* --- Mobile Filters Drawer --- */}
			{showMobileFilters && (
				<div className="fixed inset-0 z-50 flex md:hidden">
					<div
						className="fixed inset-0 bg-black/50 backdrop-blur-sm"
						onClick={() => setShowMobileFilters(false)}
					/>
					<div className="relative w-4/5 max-w-xs bg-white h-full shadow-2xl p-6 flex flex-col animate-in slide-in-from-left">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-lg font-bold text-[#051449]">
								Filters
							</h2>
							<button
								onClick={() => setShowMobileFilters(false)}
								className="p-1 hover:bg-gray-100 rounded-full"
							>
								<X className="w-6 h-6 text-gray-500" />
							</button>
						</div>

						<div className="flex-1 overflow-y-auto space-y-8">
							<div>
								<h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
									Sort By
								</h3>
								<select
									value={sort}
									onChange={(e) => {
										setSort(e.target.value);
										setPage(1);
									}}
									className="w-full p-2 bg-gray-50 border rounded-lg text-sm"
								>
									<option value="newest">
										Newest Arrivals
									</option>
									<option value="price_asc">
										Price: Low to High
									</option>
									<option value="price_desc">
										Price: High to Low
									</option>
									<option value="popular">
										Most Popular
									</option>
								</select>
							</div>

							<div>
								<h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
									Categories
								</h3>
								<div className="space-y-2">
									<button
										onClick={() => {
											setCategory(null);
											setShowMobileFilters(false);
										}}
										className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${
											!category
												? 'bg-blue-50 text-blue-700 font-medium'
												: 'text-gray-600'
										}`}
									>
										All Categories
									</button>
									{BUSINESS_TYPES.map((c) => (
										<button
											key={c}
											onClick={() => {
												setCategory(c);
												setShowMobileFilters(false);
											}}
											className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${
												c === category
													? 'bg-blue-50 text-blue-700 font-medium'
													: 'text-gray-600'
											}`}
										>
											{c}
										</button>
									))}
								</div>
							</div>

							<div>
								<h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
									Price Range
								</h3>
								<div className="grid grid-cols-2 gap-3">
									<input
										type="number"
										placeholder="Min"
										value={minPrice}
										onChange={(e) =>
											setMinPrice(Number(e.target.value))
										}
										className="w-full p-2 border rounded-lg text-sm"
									/>
									<input
										type="number"
										placeholder="Max"
										value={maxPrice}
										onChange={(e) =>
											setMaxPrice(Number(e.target.value))
										}
										className="w-full p-2 border rounded-lg text-sm"
									/>
								</div>
							</div>
						</div>

						<div className="mt-6 pt-4 border-t">
							<button
								onClick={clearAllFilters}
								className="w-full py-3 bg-[#051449] text-white rounded-xl font-bold"
							>
								Show Results
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

/* --------------------
   Product Card Component
   -------------------- */
function ProductCard({
	product,
	onAddToCart,
	isAdded,
}: {
	product: Product;
	onAddToCart: (e: React.MouseEvent) => void;
	isAdded: boolean;
}) {
	const imageUrl = Array.isArray(product.images)
		? (product.images[0] as any)?.url ?? product.images[0]
		: product.images;

	const storeName =
		typeof product.store === 'object'
			? product.store?.name
			: 'Yunimall Store';
	// Display Category Name (Handle populated object)
	const categoryName =
		typeof product.category === 'object'
			? product.category?.name
			: product.category;

	return (
		<Link
			href={`/products/${product._id}`}
			className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full"
		>
			<div className="relative aspect-[4/4] overflow-hidden bg-gray-100">
				{imageUrl ? (
					<img
						src={
							typeof imageUrl === 'string' ? imageUrl : ''
						}
						alt={product.name}
						className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center text-gray-300">
						<Store className="w-10 h-10" />
					</div>
				)}

				{product.stock === 0 && (
					<div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">
						Out of Stock
					</div>
				)}
			</div>

			<div className="p-4 flex flex-col flex-1">
				<div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
					<Store className="w-3 h-3" />
					<span className="truncate max-w-[150px]">
						{storeName}
					</span>
				</div>

				<h3
					className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-[#051449] transition-colors"
					title={product.name}
				>
					{product.name}
				</h3>

				{categoryName && (
					<div className="mb-3">
						<span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full uppercase tracking-wider font-medium">
							{categoryName}
						</span>
					</div>
				)}

				<div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-50">
					<div>
						<span className="block text-xs text-gray-400">
							Price
						</span>
						<span className="text-lg font-bold text-[#051449]">
							₦{product.price.toLocaleString()}
						</span>
					</div>

					<button
						onClick={onAddToCart}
						disabled={product.stock === 0}
						className={`p-2.5 rounded-xl transition-all shadow-sm ${
							isAdded
								? 'bg-green-500 text-white'
								: 'bg-[#FFCF23] text-[#051449] hover:bg-[#ffbf00] hover:scale-105 active:scale-95'
						} disabled:opacity-50 disabled:cursor-not-allowed`}
					>
						{isAdded ? (
							<Star className="w-5 h-5 fill-current" />
						) : (
							<ShoppingCart className="w-5 h-5" />
						)}
					</button>
				</div>
			</div>
		</Link>
	);
}
